import React, { useMemo, useEffect, useCallback } from 'react';
import { IsJsonString } from 'helpers';
import { isArray, isEmpty, isEqual, isObject, isPlainObject, uniqBy } from 'lodash';
import ColumnContactModel from 'models/column-contact.model';
import ColumnMonitoringModel, { enumColumn } from 'models/column-monitoring.model';
import getUser from 'redux/selector/getUser';
import ManageContactServices from 'services/ManageContactServices';
import { showError } from 'helpers/handleError';
import { useDispatch } from 'react-redux';
import actions from 'redux/actions';

export const useColumnsHandler = () => React.useContext(ColumnsHandlerContext);

const checkFieldAvailable = ({ data = [], field = 'value', value = '' }) => {
  return !!data.find((el) => el?.[field] === value);
};

const defaultColumns = new ColumnMonitoringModel().columns;

const { saveLayoutMonitoring, saveLayoutContactGroup } = actions;

const ColumnsHandlerContext = React.createContext({
  contactGroupLayout: [],
  monitoringV1Layout: [],
  monitoringV2Layout: [],
  /**
   *
   * @param {*} nextColumns
   * @param {Object} options
   * @param {"monitoring-v1" | "monitoring-v2" | "contact-group"} options.type
   * @param {() => void} options.onSuccess
   * @param {(error) => void} options.onFailed
   */
  save: (nextColumns, { type, onSuccess, onFailed }) => {},
});

const ColumnsHandlerProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = getUser();
  const campaignMonitoringLayout = userInfo?.settings?.campaignMonitoringLayout || '';
  const contactLayout = userInfo?.layoutContact;

  //! Fix bug for some reason, one column is removed
  useEffect(() => {
    if (!userInfo.isLogged) {
      return;
    }

    if (campaignMonitoringLayout && IsJsonString(campaignMonitoringLayout)) {
      const parsedCampaignMonitoringLayout = JSON.parse(campaignMonitoringLayout);

      if (isArray(parsedCampaignMonitoringLayout)) {
        //! For migrating response data from old version -> new version
        const layouts = {
          v1: parsedCampaignMonitoringLayout,
          v2: [],
        };

        dispatch(saveLayoutMonitoring({ nextColumns: layouts }));
        return;
      } else if (isPlainObject(parsedCampaignMonitoringLayout)) {
        const layouts = {
          v1: parsedCampaignMonitoringLayout?.v1,
          v2: parsedCampaignMonitoringLayout?.v2,
        };
        let needUpdate = false;

        const handleColumnsV1 = () => {
          const layoutV1 = layouts?.v1 || [];
          console.log('layoutV1', layoutV1);
          //* Fix bug TA-10624 - https://textback.atlassian.net/browse/TA-10642
          const fieldNeedCheck = [
            enumColumn.progress,
            enumColumn.timeRemaining,
            enumColumn.speed,
            enumColumn.profit,
            enumColumn.activeCallCount,
            enumColumn.ROI,
            enumColumn.revenuePerMilMinute,
            enumColumn.revenuePerMilMessage,
            enumColumn.revenuePerCalls,
            enumColumn.callRate,
          ];

          fieldNeedCheck.forEach((field) => {
            const isFieldNotFound = !checkFieldAvailable({
              data: layoutV1,
              value: field,
            });

            if (isFieldNotFound) {
              needUpdate = true;

              const temp = {
                column: defaultColumns?.find((el) => el.value === field)?.column,
                value: field,
                isChecked: true,
                isSortable: true,
                width: '200px',
              };

              layoutV1.push(temp);
            }
          });

          //* Check group cost
          const costGrouped = layoutV1.find((el) => el.value === enumColumn.cost);
          const fieldInCostNeedCheck = [enumColumn.amdCost];

          if (costGrouped) {
            fieldInCostNeedCheck.forEach((field) => {
              const isFieldNotFound = !costGrouped?.subColumn?.find((col) => col.value === field);

              if (isFieldNotFound) {
                needUpdate = true;
                const defaultCostGrouped = defaultColumns?.find(
                  (el) => el.value === enumColumn.cost
                );

                const temp = {
                  column: defaultCostGrouped?.subColumn?.find((el) => el.value === field)?.column,
                  value: field,
                  isChecked: true,
                  isChild: true,
                  isSortable: true,
                  width: '140px',
                };

                costGrouped.subColumn.push(temp);
              }
            });
          }

          //* Add callbackCount to statistic
          const statistic = layoutV1.find((el) => el.value === enumColumn.statistic);
          const fieldInStatisticsNeedCheck = [
            enumColumn.callbackCount,
            enumColumn.listenCounter,
            enumColumn.callDurationInMinute,
            enumColumn.callDurationInSecond,
            enumColumn.ivrClickRate,
            enumColumn.ivrOptoutRate,
            enumColumn.amd,
            enumColumn.rejected,
          ];

          if (statistic) {
            fieldInStatisticsNeedCheck.forEach((field) => {
              const isFieldNotFound = !statistic?.subColumn?.find((col) => col.value === field);

              if (isFieldNotFound) {
                needUpdate = true;
                const defaultStatistic = defaultColumns?.find(
                  (el) => el.value === enumColumn.statistic
                );

                const temp = {
                  column: defaultStatistic?.subColumn?.find((el) => el.value === field)?.column,
                  value: field,
                  isChecked: true,
                  isChild: true,
                  isSortable: true,
                  width: '80px',
                };

                statistic.subColumn.push(temp);
              }
            });

            //* Disabled statistic sort -> Server is no longer support sort by stats
            if (statistic?.subColumn?.some((el) => el?.isSortable)) {
              needUpdate = true;
              statistic?.subColumn?.forEach((el) => {
                el.isSortable = false;
              });
            }
          }
        };

        const handleColumnsV2 = () => {
          //TODO
        };

        handleColumnsV1();
        handleColumnsV2();

        if (needUpdate) {
          dispatch(saveLayoutMonitoring({ nextColumns: layouts }));
        }
      }
    }
  }, [userInfo.isLogged, campaignMonitoringLayout, dispatch]);

  //! Contact layout
  useEffect(() => {
    if (!userInfo.isLogged) {
      return;
    }

    //! Migration
    //* Filter all customTags
    (async () => {
      try {
        const response = await ManageContactServices.getContactTagsFields();
        const responseFields = isObject(response?.data) ? response.data : {};

        const mappingToDefaultColumns = Object.entries(responseFields).map(([value, column]) => {
          return {
            column,
            value,
            isChecked: false,
          };
        });

        //* Initiate contact layout
        if (isEmpty(contactLayout)) {
          dispatch(
            saveLayoutContactGroup({
              nextColumns: uniqBy(
                [...ColumnContactModel.getDefaultColumns(), ...mappingToDefaultColumns],
                'value'
              ),
            })
          );

          return;
        }

        //* Mix prev columns with response from api columns
        const mixPrevColumnsAndApiColumns = uniqBy(
          [...contactLayout, ...mappingToDefaultColumns],
          'value'
        );

        //* If prev columns and next columns is the same -> break
        if (isEqual(contactLayout, mixPrevColumnsAndApiColumns)) {
          return;
        }

        dispatch(saveLayoutContactGroup({ nextColumns: mixPrevColumnsAndApiColumns }));
      } catch (error) {
        showError(error);
      }
    })();
  }, [userInfo.isLogged, contactLayout, dispatch]);

  //! Function
  const save = useCallback(
    (nextColumns, { type, onSuccess, onFailed } = {}) => {
      if (type === 'contact-group') {
        dispatch(
          saveLayoutContactGroup({ nextColumns: nextColumns, callbacks: { onSuccess, onFailed } })
        );
        return;
      }

      const layouts = {
        v1: userInfo?.monitoringV1Layout,
        v2: userInfo?.monitoringV2Layout,
      };

      if (type === 'monitoring-v1') {
        layouts.v1 = nextColumns;
        dispatch(
          saveLayoutMonitoring({ nextColumns: layouts, callbacks: { onSuccess, onFailed } })
        );
      }

      if (type === 'monitoring-v2') {
        layouts.v2 = nextColumns;
        dispatch(
          saveLayoutMonitoring({ nextColumns: layouts, callbacks: { onSuccess, onFailed } })
        );
      }
    },
    [userInfo]
  );

  const value = useMemo(() => {
    return {
      save,
      contactGroupLayout: userInfo?.layoutContact,
      monitoringV1Layout: userInfo?.monitoringV1Layout,
      monitoringV2Layout: userInfo?.monitoringV2Layout,
    };
  }, [userInfo, save]);

  return <ColumnsHandlerContext.Provider value={value}>{children}</ColumnsHandlerContext.Provider>;
};

export default ColumnsHandlerProvider;
